import { isNil, last } from 'lodash'

import Cookie from 'lib/cookie'
import { LOAD_NUMBER } from 'config'
import { yayApi } from '../baseApi'
import { getDeviceId } from 'lib/user'
import { axiosBaseQuery, axiosShortQuery } from 'store/libs/baseQuery'
import {
  AdditonalNotificationSetting,
  FootPrint,
  RequestSNSToken,
  Review,
  SNSSignIn,
  TwoFaSecret,
  User,
  UserInterests,
  YayApiResponse,
} from 'lib/_types'
import {
  pathApiUsersGetCustomDefinitions,
  pathApiUsersChangeEmail,
  pathApiUsersEmailGrantTokens,
  pathApiUsersEmailVerificationUrls,
  pathApiUsersFootprints,
  pathApiUsersFootprintsShow,
  pathApiUsersLoginUpdate,
  pathApiUsersResetPassword,
  pathApiUsersReviewsShow,
  pathApiUsersShow,
  pathApiUsersRemoveProfilePhoto,
  pathApiUsersRemoveCoverImage,
  pathApiUsersEditV3,
  pathApiUsersDiscover,
  pathApiUsersHimaUsers,
  pathApiUsersHima,
  pathApiUsersBlock,
  pathApiUsersUnblock,
  pathApiUsersRecent,
  pathApiUsersSearch,
  pathApiUsersVipUsers,
  pathSelfApiUsersLanguage,
  pathApiUsersForgotPassword,
  pathApiUsersFollowRequestsCount,
  pathApiUsersActiveFollowings,
  pathApiUsersFollowers,
  pathApiUsersWebFollowers,
  pathApiUsersFollowRecommended,
  pathApiUsersInterests,
  pathApiUsersPolicyAgreement,
  pathApiUsersPolicyAgreementTerms,
  pathApiUsersPolicyAgreementPrivacy,
  pathApiUsersDeviceTokensNew,
  pathApiChatableFollowing,
  pathApiUsersAlive,
  pathApiUsersDefaultSettings,
  pathApiUsersSecret,
  pathApiUsersSecretEnable,
  pathApiUsersSecretDisable,
  pathApiOneTimePassword,
  pathApiUsersPolicyAgreementEmpl,
} from 'lib/paths'

const provideUserTags: any =
  (tagName: any, dataKey = 'users') =>
  (result: any) => {
    return result?.[dataKey]
      ? [
          ...result?.[dataKey].map(({ id }) => ({
            type: tagName,
            id,
          })),
          { type: tagName, id: 'LIST' },
        ]
      : { type: tagName, id: 'LIST' }
  }

export const userApi = yayApi
  .enhanceEndpoints({
    addTagTypes: [
      'Users',
      'User/Me',
      'User/Reviews',
      'User/Blocks',
      'User/Groups',
      'User/FollowRequest',
      'User/FollowRequestCount',
      'User/RefreshCounters',
      'User/NotificationSettings',
      'User/Followers',
      'User/Interests',
      'Groups/Mine',
      'Groups/Mine/Pinned',
      'User/Base',
      'UsersDiscover',
      'HimanowUser',
      'RecentUser',
      'VipUser',
      'ActiveFollowings',
      'FollowRecommended',
      'PublicTimeline',
      'HotPostTimeline',
      'NoReplyPublicTimeline',
      'User/PolicyAgreement',
      'User/AdditonalNotificationSetting',
      'User/DefaultSettings',
      'User/Secret',
    ],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getCurrentUser: build.query<User, string | void>({
        providesTags: ['User/Me'],
        query: () => ({
          url: `/v2/users/fresh/${Cookie.userId.get()}`,
        }),
        transformResponse(baseQueryReturnValue, _meta, _arg) {
          const data = baseQueryReturnValue?.user

          return {
            ...data,
            email: baseQueryReturnValue?.email,
            birthdate: baseQueryReturnValue?.birth_date,
            facebook_connected: baseQueryReturnValue?.facebook_connected,
            apple_connected: baseQueryReturnValue?.apple_connected,
            google_connected: baseQueryReturnValue?.google_connected,
            line_connected: baseQueryReturnValue?.line_connected,
            twitter_connected: Boolean(baseQueryReturnValue?.twitter_id),
            world_id_connected: baseQueryReturnValue?.world_id_connected,
            interests_count: baseQueryReturnValue?.interests_count,
            vip_until: baseQueryReturnValue?.vip_until,
          }
        },
      }),

      changeLanguage: build.mutation<{}, string>({
        queryFn: async (data, api) => {
          const formData = new FormData()

          formData.set('language', data)
          formData.set('uuid', getDeviceId())

          return await axiosBaseQuery({
            client: 'self',
          })(
            {
              url: pathSelfApiUsersLanguage,
              method: 'PUT',
              data: formData,
            },
            api
          )
        },
        invalidatesTags: ['User/Me'],
      }),

      getOneTimePassword: build.query<User, string | void>({
        query: () => ({
          url: pathApiOneTimePassword,
          method: 'GET',
        }),
        transformResponse(baseQueryReturnValue, _meta, _arg) {
          const data = baseQueryReturnValue?.require_otp

          return data
        },
      }),

      verifyOneTimePassword: build.mutation<
        any,
        {
          emailGrantToken: string
        }
      >({
        query: ({ emailGrantToken }) => ({
          url: pathApiOneTimePassword,
          method: 'POST',
          data: {
            email_grant_token: emailGrantToken,
          },
        }),
      }),

      forgotPassword: build.mutation<
        {},
        {
          email: string
        }
      >({
        query: ({ email }) => ({
          url: pathApiUsersForgotPassword,
          method: 'POST',
          data: {
            email,
          },
        }),
      }),

      requestSnsToken: build.query({
        queryFn: axiosShortQuery<RequestSNSToken>(
          () => ({
            url: '/api/request_sns_token',
          }),
          'self'
        ),
      }),

      snsSignIn: build.mutation({
        queryFn: axiosShortQuery<SNSSignIn>(
          (data) => ({
            url: '/api/sign_in_with_sns',
            method: 'POST',
            data,
          }),
          'self'
        ),
      }),

      signUp: build.mutation({
        queryFn: axiosShortQuery(
          (data) => ({
            url: '/api/sign_up',
            method: 'POST',
            data,
          }),
          'self'
        ),
      }),

      linkSns: build.mutation({
        queryFn: axiosShortQuery(
          (data) => ({
            url: '/api/link_sns',
            method: 'POST',
            data,
          }),
          'self'
        ),
        invalidatesTags: ['User/Me'],
      }),

      unlinkSns: build.mutation({
        query: (data) => ({
          url: 'v2/users/sns/disconnect',
          method: 'POST',
          data,
        }),
        invalidatesTags: ['User/Me'],
      }),

      updateUser: build.mutation({
        query: (data) => ({
          url: 'v1/users/edit',
          method: 'POST',
          data,
        }),
        invalidatesTags: ['User/Me'],
      }),

      updateUserV3: build.mutation({
        queryFn: async (data, api) => {
          return await axiosBaseQuery({
            client: 'yayJWT',
          })(
            {
              url: pathApiUsersEditV3,
              method: 'POST',
              data,
            },
            api
          )
        },
        invalidatesTags: [{ type: 'User/Base' }, 'User/Me'],
      }),

      deleteUserProfileImage: build.mutation({
        query: (data) => ({
          url: pathApiUsersRemoveProfilePhoto,
          method: 'POST',
          data,
        }),
        invalidatesTags: [{ type: 'User/Base' }, 'User/Me'],
      }),

      deleteUserCoverImage: build.mutation({
        query: (data) => ({
          url: pathApiUsersRemoveCoverImage,
          method: 'POST',
          data,
        }),
        invalidatesTags: [{ type: 'User/Base' }, 'User/Me'],
      }),

      getFootPrintList: build.query<
        Array<FootPrint>,
        {
          mode: null | string
          fromId: string
        }
      >({
        query: ({ mode, fromId }) => ({
          url: pathApiUsersFootprints,
          params: {
            number: LOAD_NUMBER,
            ...(isNil(mode) ? {} : { mode }),
            ...(fromId != '0' ? { from_id: fromId } : {}),
          },
        }),
        transformResponse(baseQueryReturnValue) {
          return baseQueryReturnValue?.footprints || []
        },
        keepUnusedDataFor: 0,
      }),

      deleteFootPrint: build.mutation<
        any,
        {
          userId: number
          footprintId: number
        }
      >({
        query: ({ userId, footprintId }) => ({
          url: pathApiUsersFootprintsShow({
            userId,
            footprintId,
          }),
          method: 'DELETE',
        }),
      }),

      getReviews: build.query<
        {
          pinned_reviews: Array<Review>
          reviews: Array<Review>
        },
        {
          userId: string
          fromId?: string
          notActive?: boolean
        }
      >({
        query: ({ userId, fromId, notActive }) => ({
          url: pathApiUsersReviewsShow({ userId }),
          params: {
            number: LOAD_NUMBER,
            not_active: notActive,
            ...(fromId != '0' ? { from_id: fromId } : {}),
          },
        }),
        providesTags: provideUserTags('User/Reviews', 'reviews'),
        keepUnusedDataFor: 0,
      }),

      createReview: build.mutation<
        any,
        {
          userId: number
          comment: string
        }
      >({
        query: ({ userId, comment }) => ({
          url: pathApiUsersReviewsShow({ userId }),
          method: 'POST',
          data: {
            comment,
          },
        }),
      }),

      deleteReview: build.mutation({
        query: (reviewId) => ({
          url: pathApiUsersReviewsShow({ userId: reviewId }),
          method: 'DELETE',
        }),
      }),

      pinReview: build.mutation({
        query: (reviewId) => ({
          url: '/v1/pinned/reviews',
          method: 'POST',
          data: {
            id: reviewId,
          },
        }),
      }),

      unpinReview: build.mutation({
        query: (reviewId) => ({
          url: `v1/pinned/reviews/${reviewId}`,
          method: 'DELETE',
        }),
      }),

      pinGroup: build.mutation({
        query: (groupId) => ({
          url: '/v1/pinned/groups',
          method: 'POST',
          data: {
            id: groupId,
          },
        }),
        invalidatesTags: [
          { type: 'User/Groups', id: 'LIST' },
          'Groups/Mine/Pinned',
        ],
      }),

      unpinGroup: build.mutation({
        query: (groupId) => ({
          url: `v1/pinned/groups/${groupId}`,
          method: 'DELETE',
        }),
        invalidatesTags: [{ type: 'User/Groups', id: 'LIST' }],
      }),

      changePassword: build.mutation({
        query: ({ currentPassword, password }) => ({
          url: '/v1/users/change_password',
          method: 'PUT',
          data: {
            current_password: currentPassword,
            password,
            api_key: window?.['gon']?.API_KEY,
          },
        }),
      }),

      changeEmail: build.mutation({
        query: (data: {
          email: string
          password: string
          email_grant_token?: string
        }) => ({
          url: pathApiUsersChangeEmail,
          method: 'PUT',
          data: {
            api_key: window?.['gon']?.API_KEY,
            ...data,
          },
        }),
        invalidatesTags: ['User/Me'],
      }),

      usersReferrals: build.mutation({
        query: ({ code }) => ({
          url: '/v1/users/referrals',
          method: 'POST',
          data: {
            code,
          },
        }),
      }),

      getBlockUsers: build.query({
        query: ({ fromId }) => ({
          url: '/v2/users/blocked',
          params: {
            from_id: fromId,
          },
        }),
        providesTags: provideUserTags('User/Blocks'),
      }),

      blockUser: build.mutation({
        query: ({ userId }) => ({
          url: pathApiUsersBlock({ userId }),
          method: 'POST',
        }),
        invalidatesTags: [{ type: 'User/Blocks', id: 'LIST' }],
      }),

      unBlockUser: build.mutation({
        query: (userId) => ({
          url: pathApiUsersUnblock({ userId }),
          method: 'POST',
        }),
        invalidatesTags: [{ type: 'User/Blocks', id: 'LIST' }],
      }),

      getSetting: build.query<{ setting: AdditonalNotificationSetting }, void>({
        providesTags: ['User/AdditonalNotificationSetting'],
        query: () => ({
          url: '/v1/users/additonal_notification_setting',
        }),
      }),

      updateSetting: build.mutation({
        query: ({ mode, on }) => ({
          url: '/v1/users/additonal_notification_setting',
          method: 'POST',
          params: {
            mode,
            on,
          },
        }),
        invalidatesTags: ['User/Me', 'User/AdditonalNotificationSetting'],
      }),

      getRefreshCounterRequests: build.query({
        providesTags: ['User/RefreshCounters'],
        query: () => ({
          url: 'v1/users/reset_counters',
        }),
      }),

      refreshCounter: build.mutation({
        invalidatesTags: ['User/RefreshCounters'],
        query: (counter) => ({
          url: 'v1/users/reset_counters',
          method: 'POST',
          data: {
            counter,
          },
        }),
      }),

      destroyUser: build.mutation<any, void>({
        query: () => ({
          url: '/v1/users/destroy',
          method: 'POST',
        }),
      }),

      getFollowRequestList: build.query({
        query: ({ fromTimestamp }) => ({
          url: '/v2/users/follow_requests',
          params: {
            from_timestamp: fromTimestamp,
            number: LOAD_NUMBER,
          },
        }),
        providesTags: [{ type: 'User/FollowRequest', id: 'LIST' }],
      }),

      getFollowRequestCount: build.query<number, {}>({
        query: () => ({
          url: pathApiUsersFollowRequestsCount,
        }),
        providesTags: ['User/FollowRequestCount'],
        keepUnusedDataFor: 0,
        transformResponse: (response) => response?.users_count ?? 0,
      }),

      updateFollowRequest: build.mutation({
        query: ({ userId, action }) => ({
          url: `/v2/users/${userId}/follow_request`,
          method: 'POST',
          params: {
            action,
          },
        }),
        invalidatesTags: [
          { type: 'User/FollowRequest', id: 'LIST' },
          'User/FollowRequestCount',
        ],
      }),

      listUserFollowings: build.query<
        {
          last_follow_id: number
          result: 'success'
          users: Array<User>
        },
        any
      >({
        query: ({ userId, fromFollowId, nickname }) => ({
          url: `/v2/users/${userId}/web_followings`,
          params: {
            number: LOAD_NUMBER,
            ...(nickname ? { ['user[nickname]']: nickname } : {}),
            ...(fromFollowId != '0' ? { from_follow_id: fromFollowId } : {}),
          },
        }),
        providesTags: provideUserTags('Users'),
      }),

      listUserFollowers: build.query<
        {
          last_follow_id: number
          result: 'success'
          users: Array<User>
        },
        any
      >({
        query: ({ userId, fromFollowId }) => ({
          url: pathApiUsersWebFollowers({ userId }),
          params: {
            number: LOAD_NUMBER,
            ...(fromFollowId != '0' ? { from_follow_id: fromFollowId } : {}),
          },
        }),
        providesTags: provideUserTags('Users'),
      }),

      listChatableUserFollowings: build.query<
        YayApiResponse<{ users: Array<User> }>,
        {
          from_timestamp?: string | number
          nickname?: string
        }
      >({
        query: ({ from_timestamp, nickname }) => ({
          url: pathApiChatableFollowing,
          method: 'POST',
          params: {
            number: LOAD_NUMBER,
            ...(nickname && { ['user[nickname]']: nickname }),
            ...(from_timestamp != '0' && { from_timestamp: from_timestamp }),
            order_by: 'last_loggedin_at',
          },
        }),
        providesTags: provideUserTags('Users'),
      }),

      getFollowers: build.query<
        {
          last_follow_id: number
          next_page_value: number
          users: Array<User>
        },
        {
          userId: number
          followedByMe?: boolean
          fromFollowId?: string
        }
      >({
        providesTags: ['User/Followers'],
        query: ({ userId, followedByMe, fromFollowId }) => ({
          url: pathApiUsersFollowers({ userId }),
          params: {
            number: LOAD_NUMBER,
            ...(followedByMe ? { followed_by_me: followedByMe } : {}),
            ...(fromFollowId != '0' ? { from_follow_id: fromFollowId } : {}),
          },
        }),
        keepUnusedDataFor: 0,
      }),

      searchMentionableUsers: build.query<
        {
          next_page_value: number
          result: 'success'
          list: Array<User>
        },
        {
          search: string
          userId: number
          groupId?: string
          from?: string
        }
      >({
        queryFn: async (
          { userId, groupId, search, from },
          _,
          __,
          baseQuery
        ) => {
          if (groupId) {
            const res = await baseQuery({
              url: `/v2/groups/${groupId}/members`,
              params: {
                number: LOAD_NUMBER,
                ...(search ? { keyword: search } : {}),
                ...(from ? { from_id: from } : {}),
              },
            })

            if (res.data) {
              const list = (res.data?.group_users || []).map(
                (d: any) => d.user
              ) as User[]

              return {
                data: {
                  list,
                  next_page_value: last(list)?.id,
                  result: 'success',
                },
              }
            }
          } else {
            const res = await baseQuery({
              url: `/v2/users/${userId}/web_followings`,
              params: {
                number: LOAD_NUMBER,
                ...(search ? { ['user[nickname]']: search } : {}),
                ...(from ? { from_follow_id: from } : {}),
              },
            })

            if (res.data) {
              const list = res.data?.users || []

              return {
                data: {
                  list,
                  next_page_value: res.data?.last_follow_id,
                  result: 'success',
                },
              }
            }
          }
        },
      }),

      followUser: build.mutation({
        query: (userId) => ({
          url: `/v2/users/${userId}/follow`,
          method: 'POST',
          data: {
            userId,
          },
        }),
        onQueryStarted(userId, { dispatch, queryFulfilled }) {
          // Hack: cannot clear cache immediately, sometime server delay around 500ms after update.
          queryFulfilled.then(() => {
            setTimeout(() => {
              dispatch(
                userApi.util.invalidateTags([{ type: 'User/Base', id: userId }])
              )
            }, 500)
          })
        },
      }),

      unFollowUser: build.mutation({
        query: (userId) => ({
          url: `/v2/users/${userId}/unfollow`,
          method: 'POST',
          data: {
            userId,
          },
        }),
        async onQueryStarted(userId, { dispatch, queryFulfilled }) {
          // Hack: cannot clear cache immediately, sometime server delay around 500ms after update.
          queryFulfilled.then(() => {
            setTimeout(() => {
              dispatch(
                userApi.util.invalidateTags([{ type: 'User/Base', id: userId }])
              )
            }, 500)
          })
        },
      }),

      emailVerificationUrls: build.mutation<
        string,
        { email: string; locale: string; intent?: 'password_reset' | 'otp' }
      >({
        query: (params) => ({
          url: pathApiUsersEmailVerificationUrls,
          method: 'POST',
          data: {
            ...params,
            device_uuid: getDeviceId(),
          },
        }),
        transformResponse: (res: { url: string }) => res.url,
      }),

      emailVerification: build.mutation({
        queryFn: axiosShortQuery(
          (url: string) => ({
            url,
            method: 'POST',
          }),
          'idCardCheck',
          () => true
        ),
      }),

      emailGrantTokens: build.mutation({
        queryFn: axiosShortQuery(
          (data: { email: string; code: string }) => ({
            url: pathApiUsersEmailGrantTokens,
            method: 'POST',
            data,
          }),
          'idCardCheck',
          (res: { email_grant_token: string }) => res.email_grant_token
        ),
      }),

      loginUpdate: build.mutation({
        query: (data: {
          email: string
          password: string
          email_grant_token: string
        }) => ({
          url: pathApiUsersLoginUpdate,
          method: 'POST',
          data: {
            api_key: window?.['gon']?.API_KEY,
            ...data,
          },
        }),
        invalidatesTags: ['User/Me'],
      }),

      resetPassword: build.mutation({
        query: (data: {
          email: string
          password: string
          email_grant_token: string
        }) => ({
          url: pathApiUsersResetPassword,
          method: 'PUT',
          data,
        }),
      }),

      getUser: build.query<User, { userId: string }>({
        providesTags: (_0, _1, args) => {
          return [{ type: 'User/Base', id: args?.userId }]
        },
        query: ({ userId }) => ({
          url: pathApiUsersShow({ userId }),
        }),
        keepUnusedDataFor: 0,
        transformResponse: (response) => response?.user ?? {},
      }),

      getUserCustomDefinitions: build.mutation<
        YayApiResponse<{
          age: number
          created_at: number
          followers_count: number
          followings_count: number
          last_loggedin_at: number
          reported_count: number
          status: 'normal' | 'temporary_ban' | 'ban' | 'not_found'
        }>,
        {}
      >({
        query: () => ({
          url: pathApiUsersGetCustomDefinitions,
        }),
      }),

      getUsersDiscover: build.query<Array<User>, {}>({
        providesTags: ['UsersDiscover'],
        query: () => ({
          url: pathApiUsersDiscover,
          params: {
            number: 20,
          },
        }),
        keepUnusedDataFor: 0,
        transformResponse: (response) => response?.users ?? [],
      }),

      getHimanowUsers: build.query<
        Array<User>,
        {
          fromHimaId?: string
        }
      >({
        providesTags: ['HimanowUser'],
        query: ({ fromHimaId }) => ({
          url: pathApiUsersHimaUsers,
          params: {
            number: LOAD_NUMBER,
            ...(fromHimaId && fromHimaId != '0'
              ? {
                  from_hima_id: fromHimaId,
                }
              : {}),
          },
        }),
        keepUnusedDataFor: 0,
        transformResponse: (response) => {
          return response?.hima_users?.map((x: any) => {
            return (
              {
                ...x?.user,
                _hima_id: x?.id,
              } ?? []
            )
          })
        },
      }),

      createHimanow: build.mutation<{}, {}>({
        invalidatesTags: ['HimanowUser'],
        query: () => ({
          url: pathApiUsersHima,
          method: 'POST',
        }),
      }),

      getRecentlyLoginUsers: build.query<
        {
          users: Array<User>
          page: number
        },
        {
          page: number
        }
      >({
        providesTags: ['RecentUser'],
        query: ({ page }) => ({
          url: pathApiUsersRecent,
          params: {
            number: LOAD_NUMBER,
            page,
          },
        }),
        keepUnusedDataFor: 0,
        transformResponse: (response, _meta, args) => {
          return {
            users: response?.users ?? [],
            page: args?.page ?? 0,
          }
        },
      }),

      searchUsers: build.query<
        {
          users: Array<User>
          page: number
        },
        {
          searchValue: Object
          page: number
        }
      >({
        providesTags: ['RecentUser'],
        query: ({ searchValue, page }) => ({
          url: pathApiUsersSearch,
          params: {
            number: LOAD_NUMBER,
            ...searchValue,
            page: +page || 0,
          },
        }),
        keepUnusedDataFor: 0,
        transformResponse: (response, _meta, args) => {
          return {
            users: response?.users ?? [],
            page: args?.page ?? 0,
          }
        },
      }),

      searchRandomVipUsers: build.query<
        {
          users: Array<User>
        },
        {}
      >({
        providesTags: ['VipUser'],
        query: ({}) => ({
          url: pathApiUsersVipUsers,
          params: {
            number: LOAD_NUMBER,
          },
        }),
        keepUnusedDataFor: 0,
      }),

      getActiveFollowings: build.query<
        {
          last_loggedin_at: number
          result: string
          users: Array<User>
        },
        {
          onlyOnline?: boolean
          fromLoggedInAt?: string
        }
      >({
        providesTags: ['ActiveFollowings'],
        query: ({ onlyOnline, fromLoggedInAt }) => ({
          url: pathApiUsersActiveFollowings,
          params: {
            number: LOAD_NUMBER,
            ...(onlyOnline ? { only_online: onlyOnline } : {}),
            ...(fromLoggedInAt ? { from_loggedin_at: fromLoggedInAt } : {}),
          },
        }),
        keepUnusedDataFor: 0,
      }),

      getFollowRecommended: build.query<
        {
          users: Array<User>
        },
        {
          userId: number
        }
      >({
        providesTags: ['FollowRecommended'],
        query: ({ userId }) => ({
          url: pathApiUsersFollowRecommended({ userId }),
          params: {
            number: LOAD_NUMBER,
          },
        }),
        keepUnusedDataFor: 0,
      }),

      getUserInterests: build.query<UserInterests[], {}>({
        providesTags: ['User/Interests'],
        query: () => ({
          url: pathApiUsersInterests,
        }),
        transformResponse: (data: { interests: UserInterests[] }) =>
          data.interests,
        keepUnusedDataFor: 0,
      }),

      updateUserInterests: build.mutation<boolean, number[]>({
        query: (ids) => ({
          url: pathApiUsersInterests,
          method: 'PUT',
          data: { ids },
        }),
        transformResponse: (res: YayApiResponse) => res.result === 'success',
        invalidatesTags: [
          'User/Interests',
          'User/Me',
          'PublicTimeline',
          'HotPostTimeline',
          'NoReplyPublicTimeline',
        ],
      }),

      getUserPolicyAgreement: build.query<
        {
          latest_privacy_policy_agreed: boolean
          latest_terms_of_use_agreed: boolean
          latest_empl_2_agreed: boolean
        },
        {}
      >({
        providesTags: ['User/PolicyAgreement'],
        query: () => ({
          // force prevent cache
          url: pathApiUsersPolicyAgreement + `?t=${Date.now()}`,
        }),
        keepUnusedDataFor: 0,
      }),

      agreeUserPolicyAgreementTerm: build.mutation<boolean, {}>({
        query: () => ({
          url: pathApiUsersPolicyAgreementTerms,
          method: 'POST',
        }),
        transformResponse: (res: YayApiResponse) => res.result === 'success',
        invalidatesTags: ['User/PolicyAgreement'],
      }),

      agreeUserPolicyAgreementPrivacy: build.mutation<boolean, {}>({
        query: () => ({
          url: pathApiUsersPolicyAgreementPrivacy,
          method: 'POST',
        }),
        transformResponse: (res: YayApiResponse) => res.result === 'success',
        invalidatesTags: ['User/PolicyAgreement'],
      }),

      agreeUserPolicyNewEmpl: build.mutation<boolean, {}>({
        query: () => ({
          url: pathApiUsersPolicyAgreementEmpl,
          method: 'POST',
        }),
        transformResponse: (res: YayApiResponse) => res.result === 'success',
        invalidatesTags: ['User/PolicyAgreement'],
      }),

      savePushNotification: build.mutation<
        any,
        {
          token: string
          browser: string
        }
      >({
        query: ({ token, browser }) => ({
          url: pathApiUsersDeviceTokensNew,
          method: 'POST',
          data: {
            push_token: token,
            browser_type: browser,
          },
        }),
      }),

      usersAlive: build.mutation({
        query: () => ({
          url: pathApiUsersAlive,
          method: 'POST',
        }),
      }),

      usersDefaultSettings: build.query<
        {
          anonymous_call_settings: { enabled: boolean }
          timeline_settings: {
            faves_filter: boolean
            hide_hot_post: boolean
            hide_reply_following_timeline: boolean
            hide_reply_public_timeline: boolean
          }
        },
        {}
      >({
        providesTags: ['User/DefaultSettings'],
        query: () => ({
          // force prevent cache
          url: pathApiUsersDefaultSettings + `?t=${Date.now()}`,
        }),
        keepUnusedDataFor: 0,
      }),

      usersSecret: build.query<TwoFaSecret, void>({
        providesTags: ['User/Secret'],
        query: () => ({
          url: pathApiUsersSecret,
        }),
        keepUnusedDataFor: 0,
      }),

      usersSecretEnable: build.mutation<
        YayApiResponse & { recovery_codes: Array<string> },
        {
          code: string
        }
      >({
        query: (data) => ({
          url: pathApiUsersSecretEnable,
          method: 'PUT',
          data,
        }),
        invalidatesTags: ['User/Me'],
      }),

      usersSecretDisable: build.mutation<
        YayApiResponse,
        {
          code: string
        }
      >({
        query: (data) => ({
          url: pathApiUsersSecretDisable,
          method: 'PUT',
          data,
        }),
        invalidatesTags: ['User/Me'],
      }),
    }),
    overrideExisting: false,
  })

export const useCurrentUser = userApi.endpoints.getCurrentUser.useQueryState

export const {
  endpoints,
  useChangeLanguageMutation,
  useForgotPasswordMutation,
  useGetFootPrintListQuery,
  useDeleteFootPrintMutation,
  useChangePasswordMutation,
  useChangeEmailMutation,
  useUsersReferralsMutation,
  useGetReviewsQuery,
  useCreateReviewMutation,
  useDeleteReviewMutation,
  usePinReviewMutation,
  useUnpinReviewMutation,
  usePinGroupMutation,
  useUnpinGroupMutation,
  useGetSettingQuery,
  useUpdateSettingMutation,
  useUpdateUserMutation,
  useUpdateUserV3Mutation,
  useDeleteUserProfileImageMutation,
  useDeleteUserCoverImageMutation,
  useGetRefreshCounterRequestsQuery,
  useRefreshCounterMutation,
  useDestroyUserMutation,
  useGetBlockUsersQuery,
  useBlockUserMutation,
  useUnBlockUserMutation,
  useGetFollowRequestListQuery,
  useGetFollowRequestCountQuery,
  useUpdateFollowRequestMutation,
  useListUserFollowersQuery,
  useListChatableUserFollowingsQuery,
  useGetFollowersQuery,
  useListUserFollowingsQuery,
  useFollowUserMutation,
  useUnFollowUserMutation,
  useSnsSignInMutation,
  useSignUpMutation,
  useLazyRequestSnsTokenQuery,
  useLinkSnsMutation,
  useSearchMentionableUsersQuery,
  useUnlinkSnsMutation,
  useEmailVerificationUrlsMutation,
  useEmailVerificationMutation,
  useEmailGrantTokensMutation,
  useLoginUpdateMutation,
  useResetPasswordMutation,
  useGetUserQuery,
  useGetUserCustomDefinitionsMutation,
  useGetUsersDiscoverQuery,
  useGetHimanowUsersQuery,
  useCreateHimanowMutation,
  useSearchUsersQuery,
  useGetRecentlyLoginUsersQuery,
  useSearchRandomVipUsersQuery,
  useGetActiveFollowingsQuery,
  useGetFollowRecommendedQuery,
  useGetCurrentUserQuery,
  useGetUserInterestsQuery,
  useUpdateUserInterestsMutation,
  useGetUserPolicyAgreementQuery,
  useAgreeUserPolicyAgreementTermMutation,
  useAgreeUserPolicyAgreementPrivacyMutation,
  useAgreeUserPolicyNewEmplMutation,
  useSavePushNotificationMutation,
  useUsersAliveMutation,
  useUsersDefaultSettingsQuery,
  useLazyUsersSecretQuery,
  useUsersSecretEnableMutation,
  useUsersSecretDisableMutation,
  useGetOneTimePasswordQuery,
  useVerifyOneTimePasswordMutation,
} = userApi
